import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { message } from "antd";
import { ContentTopic } from "api/config/chalice-types";
import { genericError } from "fixtures/globalConstants";
import { Dispatch, SetStateAction, useEffect } from "react";
import { updateBusinessDetails } from "store/business/businessActions";

interface UseTopicDeduplicationProps {
  serverTopics: ContentTopic[] | undefined; // Can be initially undefined
  currentBusinessId: string | undefined;
  dispatch: ThunkDispatch<any, undefined, AnyAction>;
  setDisplayTopics: Dispatch<SetStateAction<ContentTopic[]>>;
}

export const useTopicDeduplication = ({
  serverTopics,
  currentBusinessId,
  dispatch,
  setDisplayTopics,
}: UseTopicDeduplicationProps): void => {
  useEffect(() => {
    // Ensure serverTopics is an array before processing
    const topicsToProcess = serverTopics || [];

    if (topicsToProcess.length > 0) {
      const uniqueTopicsMap = new Map<string, ContentTopic>();
      topicsToProcess.forEach((topic) => {
        const normalizedTitle = topic.title?.trim().toLowerCase();
        if (normalizedTitle && !uniqueTopicsMap.has(normalizedTitle)) {
          uniqueTopicsMap.set(normalizedTitle, topic);
        }
      });

      const uniqueTopicsResult = Array.from(uniqueTopicsMap.values());

      if (uniqueTopicsResult.length < topicsToProcess.length) {
        const removedCount = topicsToProcess.length - uniqueTopicsResult.length;

        message.info(
          `Removed ${removedCount} duplicate topic(s) based on title.`,
          5
        );

        if (currentBusinessId) {
          // @ts-ignore - Preserve existing ignore
          dispatch(updateBusinessDetails({ topics: uniqueTopicsResult } as any))
            .unwrap()
            .then(() => {
              // Call the passed setter on success
              setDisplayTopics(uniqueTopicsResult);
            })
            .catch((error: any) => {
              console.error(
                "Deduplication: Failed to save deduplicated topics:",
                error
              );
              message.error(
                "Failed to remove duplicate topics. Displaying original list."
              );
              genericError(error);
              // On failure, set display to original server topics via passed setter
              setDisplayTopics(topicsToProcess);
            });
        } else {
          // If no business ID, call the passed setter with unique topics
          setDisplayTopics(uniqueTopicsResult);
        }
      } else {
        // No duplicates found, call setter with original processed topics
        setDisplayTopics(topicsToProcess);
      }
    } else {
      // No server topics or empty array, call setter with empty array
      setDisplayTopics([]);
    }
    // Dependencies: serverTopics array content, business ID, and dispatch function
  }, [serverTopics, currentBusinessId, dispatch, setDisplayTopics]);
};
